import { useState, useRef, type CSSProperties } from 'react';
import { useApp, Icon, Input, Button, NakedButton, MelioLogo, Field } from '@/components/ds';
import { portalLogin } from '@/api/payouts';
import type { Go } from '@/lib/types';

// signin.tsx — dedicated full-page sign-in: email + password against POST /v2/portal/login.
// (The design's 2-step verification screen is kept as dead code — the API has no 2FA yet.)

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  style?: CSSProperties;
}

function CodeInput({ value, onChange, length = 6 }: CodeInputProps) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const chars = value.padEnd(length).split('').slice(0, length);
  const setAt = (i: number, ch: string) => {
    const next = value.split('');
    next[i] = ch;
    onChange(next.join('').slice(0, length));
    if (ch && refs.current[i + 1]) refs.current[i + 1]!.focus();
  };
  const onKey = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !chars[i].trim() && refs.current[i - 1]) refs.current[i - 1]!.focus();
  };
  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const txt = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (txt) {
      e.preventDefault();
      onChange(txt);
      const last = Math.min(txt.length, length - 1);
      if (refs.current[last]) refs.current[last]!.focus();
    }
  };
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          value={chars[i].trim()}
          onChange={(e) => setAt(i, e.target.value.replace(/\D/g, '').slice(-1))}
          onKeyDown={(e) => onKey(i, e)}
          onPaste={onPaste}
          className="om-mono"
          style={{
            width: 50,
            height: 60,
            textAlign: 'center',
            fontSize: 24,
            fontWeight: 600,
            color: 'var(--text-primary)',
            border: '1.5px solid var(--border-interactive)',
            borderRadius: 12,
            background: '#fff',
            outline: 'none',
            transition: 'border-color .15s, box-shadow .15s',
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--accent)';
            e.target.style.boxShadow = '0 0 0 3px var(--accent-ring)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--border-interactive)';
            e.target.style.boxShadow = 'none';
          }}
        />
      ))}
    </div>
  );
}

interface SignInProps {
  go: Go;
  style?: CSSProperties;
}

function SignIn({ go }: SignInProps) {
  const app = useApp();
  const [step, setStep] = useState<'form' | 'twofa'>('form'); // form | twofa (twofa unused — no API support)
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e?: React.FormEvent<HTMLFormElement>) => {
    e && e.preventDefault();
    setErr('');
    if (!email) {
      setErr('Enter the email for your developer account.');
      return;
    }
    if (!pw) {
      setErr('Enter your password.');
      return;
    }
    setLoading(true);
    try {
      const { partners, accessToken } = await portalLogin({ env: app.env }, email, pw);
      const first = partners[0];
      app.signIn({
        partnerName: first?.partnerName,
        companyName: first?.companyName,
        email,
        partner: 'self-serve',
        portalToken: accessToken,
      });
      if (partners.length === 0) {
        go('/onboarding', { state: { resumeEmail: email } });
      } else {
        go('/dashboard');
      }
    } catch (ex: unknown) {
      const apiErr = ex as { status?: number; message?: string };
      setErr(apiErr?.status === 401 ? 'Invalid email or password.' : apiErr?.message || 'Sign in failed.');
      setLoading(false);
    }
  };

  const verify = () => {
    if (code.replace(/\D/g, '').length < 6) {
      setErr('Enter the 6-digit code.');
      return;
    }
    setLoading(true);
    setTimeout(() => go('/dashboard'), 650);
  };

  const PwToggle = (
    <button
      type="button"
      onClick={() => setShowPw((s) => !s)}
      style={{
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--accent-text)',
        padding: 0,
      }}
    >
      {showPw ? 'Hide' : 'Show'}
    </button>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* slim chrome — full-width app bar, matches the main TopNav across every page */}
      <div style={{ borderBottom: '1px solid var(--border-static)', flexShrink: 0 }}>
        <div style={{ padding: '0 28px', height: 64, display: 'flex', alignItems: 'center' }}>
          <button
            onClick={() => go('/')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <MelioLogo height={22} />
            <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
            <span style={{ fontSize: 14, fontWeight: 600 }}>API</span>
          </button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>New here?</span>
          <NakedButton color="var(--accent-text)" style={{ marginLeft: 12 }} onClick={() => go('/onboarding')}>
            Get an API key
          </NakedButton>
        </div>
      </div>

      <div className="grid-2" style={{ flex: 1, gridTemplateColumns: '1.15fr 0.85fr', gap: 0, alignItems: 'stretch' }}>
        {/* left form */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px', overflowY: 'auto' }}>
          <div style={{ width: '100%', maxWidth: 400 }}>
            {step === 'form' && (
              <form onSubmit={submit} style={{ animation: 'om-rise .3s ease both' }}>
                <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.02em' }}>Sign in</h1>
                <p style={{ fontSize: 15, color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  Access your dashboard, keys, and payment activity.
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                  <Field label="Work email" required>
                    <Input
                      type="email"
                      value={email}
                      onChange={setEmail}
                      placeholder="you@company.com"
                      prefix={<Icon name="email" size={16} color="var(--icon-primary)" />}
                    />
                  </Field>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <label style={{ fontSize: 13, lineHeight: '18px', fontWeight: 600, color: 'var(--text-secondary)' }}>
                        Password<span style={{ color: 'var(--text-critical)', marginLeft: 3 }}>*</span>
                      </label>
                      <span style={{ flex: 1 }} />
                      <button
                        type="button"
                        onClick={() => setErr('Password reset link sent — check your inbox.')}
                        style={{
                          border: 'none',
                          background: 'none',
                          cursor: 'pointer',
                          fontSize: 13,
                          fontWeight: 600,
                          color: 'var(--accent-text)',
                          padding: 0,
                        }}
                      >
                        Forgot password?
                      </button>
                    </div>
                    <Input type={showPw ? 'text' : 'password'} value={pw} onChange={setPw} placeholder="••••••••••••" suffix={PwToggle} />
                  </div>

                  {err && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-critical)' }}>
                      <Icon name="info-fill" size={15} color="var(--text-critical)" />
                      {err}
                    </div>
                  )}

                  <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', marginTop: -2 }}>
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      style={{ width: 17, height: 17, accentColor: 'var(--accent)' }}
                    />
                    <span style={{ fontSize: 14, color: 'var(--text-primary)' }}>Keep me signed in for 30 days</span>
                  </label>
                </div>

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  style={{ marginTop: 24 }}
                  rightIcon={loading ? undefined : 'arrow-right'}
                  disabled={loading}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>
            )}

            {step === 'twofa' && (
              <div style={{ animation: 'om-rise .3s ease both' }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 14,
                    background: 'var(--accent-soft)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: 18,
                  }}
                >
                  <Icon name="lock" size={26} color="var(--accent)" />
                </div>
                <h1 style={{ margin: 0, fontSize: 26, letterSpacing: '-0.02em' }}>Two-step verification</h1>
                <p style={{ fontSize: 15, lineHeight: '23px', color: 'var(--text-secondary)', margin: '10px 0 26px' }}>
                  Enter the 6-digit code from your authenticator app to finish signing in.
                </p>
                <CodeInput
                  value={code}
                  onChange={(v) => {
                    setCode(v);
                    setErr('');
                  }}
                />
                {err && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      fontSize: 13,
                      color: 'var(--text-critical)',
                      marginTop: 14,
                    }}
                  >
                    <Icon name="info-fill" size={15} color="var(--text-critical)" />
                    {err}
                  </div>
                )}
                <Button
                  fullWidth
                  size="large"
                  style={{ marginTop: 24 }}
                  rightIcon={loading ? undefined : 'arrow-right'}
                  disabled={loading}
                  onClick={verify}
                >
                  {loading ? 'Verifying…' : 'Verify & continue'}
                </Button>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    marginTop: 18,
                    fontSize: 13.5,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <span>Didn't get a code?</span>
                  <button
                    type="button"
                    onClick={() => {}}
                    style={{
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: 13.5,
                      fontWeight: 600,
                      color: 'var(--accent-text)',
                      padding: 0,
                    }}
                  >
                    Resend
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setStep('form');
                    setCode('');
                    setErr('');
                  }}
                  style={{
                    width: '100%',
                    marginTop: 18,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: 'var(--text-secondary)',
                  }}
                >
                  <Icon name="arrow-left" size={15} color="var(--text-secondary)" /> Back to sign in
                </button>
              </div>
            )}
          </div>
        </div>

        {/* right brand panel */}
        <div
          style={{
            background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
            padding: '56px 48px',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              position: 'absolute',
              right: -90,
              top: -70,
              width: 320,
              height: 320,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: -70,
              bottom: -90,
              width: 280,
              height: 280,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.18), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <h2 style={{ fontSize: 30, lineHeight: '38px', letterSpacing: '-0.02em', margin: '0 0 14px', color: '#fff' }}>Welcome back</h2>
            <p style={{ fontSize: 15, lineHeight: '23px', color: '#9aa3b2', margin: '0 0 36px' }}>
              Pick up where you left off — keys, webhooks, and every payment moving through your account.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {[
                ['dashboard', 'Live payment activity', 'Track ACH and check payments end to end.'],
                ['lock', 'Manage your API keys', 'Rotate, scope, and revoke keys in seconds.'],
                ['fast', 'Test & live in one place', 'Switch environments without losing context.'],
              ].map(([ic, h, s]) => (
                <div key={h} style={{ display: 'flex', gap: 14 }}>
                  <div
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: 10,
                      flexShrink: 0,
                      background: 'rgba(255,255,255,0.06)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon name={ic} size={19} color="#c9b8ff" />
                  </div>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#fff' }}>{h}</div>
                    <div style={{ fontSize: 13, lineHeight: '19px', color: '#9aa3b2', marginTop: 2 }}>{s}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 36, fontSize: 13, color: '#9aa3b2' }}>
              <Icon name="lock" size={15} color="#9aa3b2" /> Bank-grade encryption · SOC 2 Type II
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { SignIn, CodeInput };
